import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

import { authApi, vertaalApiFout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * P5: pagina om een nieuw wachtwoord in te stellen met een token uit
 * de reset-mail. Token komt mee in de URL als `?token=...`.
 *
 * Validaties (frontend, backend valideert ook):
 * - token aanwezig in URL.
 * - nieuw wachtwoord >= 12 chars.
 * - confirm-veld komt overeen met nieuw_wachtwoord.
 *
 * 2FA blijft vereist na reset — duidelijk gemeld in de footer-tekst.
 */
export function WachtwoordResetPagina() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [wachtwoord, setWachtwoord] = useState("");
  const [bevestiging, setBevestiging] = useState("");
  const [bezig, setBezig] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
        <div className="w-full max-w-sm rounded-xl border bg-white p-6 space-y-3 text-center">
          <h1 className="text-[18px] font-semibold">
            {t("auth.reset.token_ontbreekt_titel")}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t("auth.reset.token_ontbreekt_uitleg")}
          </p>
          <Link
            to="/wachtwoord-vergeten"
            className="inline-block text-[12.5px] text-brand-cyan-dark hover:underline"
          >
            {t("auth.reset.opnieuw_aanvragen")}
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (wachtwoord !== bevestiging) {
      toast.error(t("auth.reset.wachtwoorden_komen_niet_overeen"));
      return;
    }
    if (wachtwoord.length < 12) {
      toast.error(t("auth.reset.te_kort"));
      return;
    }
    setBezig(true);
    try {
      await authApi.wachtwoordReset(token, wachtwoord);
      toast.success(t("auth.reset.gelukt"));
      navigate("/login", { replace: true });
    } catch (fout) {
      toast.error(vertaalApiFout(fout));
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border bg-white p-6 space-y-5">
          <div className="text-center">
            <h1 className="text-[18px] font-semibold">
              {t("auth.reset.nieuw_wachtwoord_titel")}
            </h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              {t("auth.reset.nieuw_wachtwoord_uitleg")}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ww-nieuw">
                {t("auth.reset.nieuw_wachtwoord_label")}
              </Label>
              <Input
                id="ww-nieuw"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={wachtwoord}
                onChange={(e) => setWachtwoord(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ww-bevestig">
                {t("auth.reset.bevestiging_label")}
              </Label>
              <Input
                id="ww-bevestig"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={bevestiging}
                onChange={(e) => setBevestiging(e.target.value)}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={bezig || wachtwoord.length < 12}
            >
              {bezig && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("auth.reset.opslaan")}
            </Button>
          </form>
        </div>

        <p className="flex items-center gap-1.5 justify-center text-[12px] text-muted-foreground mt-6">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t("auth.reset.tweefa_blijft_vereist")}
        </p>
      </div>
    </div>
  );
}
